/**
 * Hook for managing message flags.
 *
 * Built-in flags (high_risk, low_confidence, asked_human) use smallint columns on `messages`.
 * Custom per-agent flags use `custom_message_flags` JSON only.
 */

import { useState, useCallback, useEffect } from "react"
import { supabase } from "@/lib/data"
import { FlagType, UseFlagsResult, DetectedItem, EvalItemMetaMap, ModerationMessage } from "../types"
import { isModerationFlagItemId, pickFlagItemIdForToggle } from "../utils/conversationUtils"
import {
  isBuiltinFlagId,
  getCustomMessageFlagsOnly,
  toggleBuiltinColumnValue,
  resolveAllBuiltinColumns,
} from "../utils/builtinFlagState"

// ── Legacy helpers ──────────────────────────────────────────────────

function applyToggleLegacy(items: DetectedItem[], itemId: number): DetectedItem[] {
  const existing = items.find(i => i.itemId === itemId)
  if (!existing) {
    return [...items, { itemId, status: "flagged" }]
  }
  return items.map(i =>
    i.itemId === itemId
      ? { ...i, status: i.status === "resolved" ? "flagged" : "resolved" }
      : i
  )
}

function applyResolveAllLegacy(items: DetectedItem[], meta: EvalItemMetaMap): DetectedItem[] {
  return items.map(i =>
    isModerationFlagItemId(i.itemId as number, meta) ? { ...i, status: "resolved" as const } : i
  )
}

// ── custom_message_flags (JSON) helpers ──────────────────────────────────

function applyToggleCustomFlag(flags: FlagDetectedItem[], flagId: string): FlagDetectedItem[] {
  const existing = flags.find(f => f.flagId === flagId)
  if (!existing) {
    return [...flags, { flagId, status: "flagged", detectedAt: new Date().toISOString() }]
  }
  return flags.map(f =>
    f.flagId === flagId
      ? { ...f, status: f.status === "resolved" ? "flagged" : "resolved" }
      : f
  )
}

function applyResolveAllCustom(flags: FlagDetectedItem[]): FlagDetectedItem[] {
  return flags.map(f => ({ ...f, status: "resolved" as const }))
}

export function useFlags(): UseFlagsResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [evalMeta, setEvalMeta] = useState<EvalItemMetaMap>({})

  useEffect(() => {
    void supabase
      .from("eval_items")
      .select("id, name, highrisk, lowconf, askhuman")
      .then(({ data }) => {
        if (!data) return
        const m: EvalItemMetaMap = {}
        for (const row of data) {
          m[row.id as number] = {
            name: row.name ?? "",
            highrisk: row.highrisk,
            lowconf: row.lowconf,
            askhuman: row.askhuman,
          }
        }
        setEvalMeta(m)
      })
  }, [])

  const toggleFlag = useCallback(async (
    messageId: string,
    flagType: FlagType,
    currentItems: DetectedItem[],
    currentFlags: FlagDetectedItem[] = [],
    message?: ModerationMessage
  ): Promise<{ items: DetectedItem[]; flags: FlagDetectedItem[] }> => {
    setLoading(true)
    setError(null)
    try {
      let updatedItems = currentItems
      const customOnly = getCustomMessageFlagsOnly(currentFlags)
      const updatePayload: Record<string, unknown> = {}

      if (isBuiltinFlagId(flagType)) {
        Object.assign(updatePayload, toggleBuiltinColumnValue(flagType, message))
        // Preserve custom-only JSON; builtins live in columns now
        updatePayload.custom_message_flags = customOnly.length > 0 ? customOnly : null

        const itemId = pickFlagItemIdForToggle(flagType, currentItems, evalMeta)
        if (itemId != null) {
          updatedItems = applyToggleLegacy(currentItems, itemId)
          updatePayload.user_detected_items = updatedItems
        }
      } else {
        updatePayload.custom_message_flags = applyToggleCustomFlag(customOnly, flagType)
      }

      const { error: err } = await supabase
        .from("messages")
        .update(updatePayload)
        .eq("id", messageId)

      if (err) throw err

      const nextCustom = (updatePayload.custom_message_flags as FlagDetectedItem[] | null) ?? []
      return { items: updatedItems, flags: nextCustom }
    } catch (err) {
      console.error("[useFlags] toggleFlag failed:", err)
      setError(err instanceof Error ? err.message : "Failed to toggle flag")
      throw err
    } finally {
      setLoading(false)
    }
  }, [evalMeta])

  const resolveAllFlags = useCallback(async (
    messageId: string,
    currentItems: DetectedItem[],
    currentFlags: FlagDetectedItem[] = [],
    message?: ModerationMessage
  ): Promise<{ items: DetectedItem[]; flags: FlagDetectedItem[] }> => {
    setLoading(true)
    setError(null)
    try {
      const customResolved = applyResolveAllCustom(getCustomMessageFlagsOnly(currentFlags))
      const updatedItems = applyResolveAllLegacy(currentItems, evalMeta)

      const updatePayload: Record<string, unknown> = {
        user_detected_items: updatedItems as unknown,
        custom_message_flags: customResolved.length > 0 ? customResolved : null,
        ...resolveAllBuiltinColumns(message),
      }

      const { error: err } = await supabase
        .from("messages")
        .update(updatePayload)
        .eq("id", messageId)

      if (err) throw err
      return { items: updatedItems, flags: customResolved }
    } catch (err) {
      console.error("[useFlags] resolveAllFlags failed:", err)
      setError(err instanceof Error ? err.message : "Failed to resolve flags")
      throw err
    } finally {
      setLoading(false)
    }
  }, [evalMeta])

  return { toggleFlag, resolveAllFlags, loading, error }
}
